import LeavesApprovalsTable from "components/leaveApprovals/parts/LeaveApprovalsTable";
import { CardLayout } from "components/CardLayout";
import LeaveRequestsLoader, {
  useLeaveRequestsLoader,
} from "./Loaders/LeaveRequestsLoader";
import { SearchControls } from "./parts/SearchControls";
import { useMemo } from "react";
import { PaginationType, addToast } from "@octano/global-ui";
import {
  ITEMS_PER_PAGE_DEFAULT,
  LeaveRequestsPaginatedResponse,
} from "./types";
import { annulLeaveRequest, getPaginatedData } from "./api";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { fetchAllUsers } from "views/LeavesReview/api";

const EXPORT_ITEMS_PER_PAGE = 200;
const EXPORT_HEADERS = [
  "Fecha de solicitud",
  "Tipo de solicitud",
  "Total días hábiles",
  "Horario Medio Día",
  "Motivo ",
  "Solicitado por",
  "RUT",
  "Solicitado a",
  "Fecha de inicio",
  "Fecha de fin",
  "Estado",
  "Resuelto por",
];

function capitalize(string: string) {
  if (!string) return "";
  return string
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const LeaveRequestsContent: React.FC = () => {
  const { data, loading, query, updateQuery, refresh } =
    useLeaveRequestsLoader();

  const pagination = useMemo<PaginationType | undefined>(() => {
    if (data) {
      return {
        totalItems: data.total,
        onChangePage: (page) => updateQuery({ ...query, page }),
        itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
        totalPages: data.total_pages,
        currentPage: query.page,
      };
    } else return undefined;
  }, [data, query, updateQuery]);

  const handleExport = async () => {
    try {
      const usersResponse = await fetchAllUsers();

      const users = usersResponse.data || [];
      const usersMap = new Map(
        users.map((user) => [user.full_name.toLowerCase(), user]),
      );

      const buildRows = (items: LeaveRequestsPaginatedResponse["data"]) => {
        return items.map((item) => {
          const user = usersMap.get(item.requestBy.toLowerCase());
          const rut = user
            ? `${user.rut}-${user.findUserOrds?.data?.[0]?.dv || ""}`
            : "";

          return [
            dayjs(item.createdAt).format("DD/MM/YYYY"),
            capitalize(item.requestType),
            item.isFullDay ? item.requestedDays : 0.5,
            !item.isFullDay ? (item.isMorning ? "Mañana" : "Tarde") : "",
            item.comment,
            capitalize(item.requestBy),
            rut,
            capitalize(item.requestTo),
            dayjs(item.startDate).format("DD/MM/YYYY"),
            item.endDate ? dayjs(item.endDate).format("DD/MM/YYYY") : "",
            item.approved === true
              ? "Aprobado"
              : item.approved === false
                ? "Rechazado"
                : "Pendiente",
            capitalize(item.doneBy || ""),
          ];
        });
      };

      const worksheet = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS]);
      const workbook = XLSX.utils.book_new();

      let currentPage = 1;
      let totalPages = 1;

      while (currentPage <= totalPages) {
        const response = await getPaginatedData({
          page: currentPage,
          limit: EXPORT_ITEMS_PER_PAGE,
          searchParams: query.searchParams,
        });

        if (response.error || !response.data) {
          addToast({
            text: "Error al descargar el reporte",
            color: "danger",
            icon: "error",
          });
          return;
        }

        totalPages = Math.max(response.data.total_pages || 1, currentPage);

        const rows = buildRows(response.data.data);

        if (rows.length) {
          XLSX.utils.sheet_add_aoa(worksheet, rows, { origin: -1 });
        }

        currentPage += 1;
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "Solicitudes");
      XLSX.writeFile(workbook, "reporte_solicitudes.xlsx");
    } catch (error) {
      console.error(error);
      addToast({
        text: "Error al descargar el reporte",
        color: "danger",
        icon: "error",
      });
    }
  };

  const handleAnnul = async (id: string, payload?: { comments?: string }) => {
    try {
      const response = await annulLeaveRequest(id, payload);
      if (!response.error && response.data?.success) {
        addToast({
          text: "La solicitud ha sido anulada exitosamente.",
          color: "success",
          icon: "success",
        });
        refresh();
      } else {
        addToast({
          text: "Hubo un problema al anular la solicitud.",
          color: "danger",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error al anular la solicitud", error);
      addToast({
        text: "Hubo un problema al anular la solicitud.",
        color: "danger",
        icon: "error",
      });
    }
  };

  return (
    <>
      <div className="text-light fw-400 fs-18 mb-5">
        A continuación se muestran las solicitudes de todos los colaboradores.
      </div>
      <SearchControls />
      <LeavesApprovalsTable
        data={data?.data || []}
        onRefresh={refresh}
        showReport={true}
        pagination={pagination}
        isLoading={loading}
        onExport={handleExport}
        handleAnnulRequest={handleAnnul}
      />
    </>
  );
};

const LeaveRequests: React.FC = () => {
  return (
    <section id="aprobaciones" className="pt-3">
      <CardLayout>
        <LeaveRequestsLoader>
          <LeaveRequestsContent />
        </LeaveRequestsLoader>
      </CardLayout>
    </section>
  );
};

export default LeaveRequests;
